import { useState, useEffect } from "react";
import { Input } from "components/ui/input";
import { useStore, store } from "lib/store/store";
import { Tool } from "lib/types";
import { useSubscriptionApi } from "lib/hooks/useApi";
import { getStoredAuthData, storeAuthData, clearAuthData } from "lib/utils";
import { tools } from "lib/data";
import { useLocation } from "react-router-dom";
import Loader from "components/ui/loader";

function isTool(value: unknown): value is Tool {
  return value === "tgb" || value === "tgv" || value === "tgt";
}

function Popup() {
  const { popupOpen, setPopupOpen, setPage, setManageToolsActiveTool } = useStore();
  const location = useLocation();
  const [paymentEmail, setPaymentEmail] = useState<string>("");
  const [otp, setOtp] = useState<string>("");
  const [otpSent, setOtpSent] = useState(false);
  const [checkingStoredAuth, setCheckingStoredAuth] = useState(false);

  const currentTool: Tool | null = isTool(popupOpen) ? popupOpen : null;
  const selectedToolInfo = currentTool ? tools.find((t) => t.id === currentTool) : null;
  const { loading, sendOtp, getSubscriptions } = useSubscriptionApi(currentTool || "tgb");

  const navigateAfterLogin = (tool: Tool) => {
    const currentPage = store.getState().store.page;
    if (currentPage === "manage-tools" || location.pathname === "/manage-tools") {
      setManageToolsActiveTool(tool);
      setPage("manage-tools");
      return;
    }
    setManageToolsActiveTool(null);
    setPage(tool);
  };

  const handleStoredAuthFetch = async (email: string, storedOtp: string, tool: Tool) => {
    const result = await getSubscriptions(email, storedOtp);
    if (result?.success) {
      storeAuthData(tool, email, storedOtp);
      setPopupOpen(false);
      navigateAfterLogin(tool);
    } else {
      clearAuthData(tool);
      setPaymentEmail("");
      setOtp("");
      setCheckingStoredAuth(false);
    }
  };

  useEffect(() => {
    if (currentTool) {
      const storedAuth = getStoredAuthData(currentTool);
      if (storedAuth) {
        setPaymentEmail(storedAuth.email);
        setOtp(storedAuth.otp);
        setCheckingStoredAuth(true);
        handleStoredAuthFetch(storedAuth.email, storedAuth.otp, currentTool);
      }
    }
    return () => {
      setPaymentEmail("");
      setOtp("");
      setOtpSent(false);
      setCheckingStoredAuth(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTool]);

  useEffect(() => {
    setOtpSent(false);
  }, [paymentEmail]);

  useEffect(() => {
    if (!popupOpen) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
    };
  }, [popupOpen]);

  const handleSendOTP = async () => {
    if (!paymentEmail || !currentTool) return;
    const result = await sendOtp(paymentEmail);
    if (result?.success) {
      setOtpSent(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!paymentEmail || !otp || !currentTool) return;
    const result = await getSubscriptions(paymentEmail, otp);
    if (result?.success) {
      storeAuthData(currentTool, paymentEmail, otp);
      setPopupOpen(false);
      navigateAfterLogin(currentTool);
    }
  };

  const closePopup = () => {
    setPopupOpen(false);
    if (location.pathname === "/manage-tools") {
      setPage("manage-tools");
    } else {
      setPage("front");
    }
  };

  if (!currentTool || !popupOpen) {
    return null;
  }

  const toolName = selectedToolInfo?.name || "Unknown Tool";
  const toolImage = selectedToolInfo?.image || "/other-tools/tg-bulk-sender.png";

  return (
    <>
      {popupOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-[var(--dash-overlay)] p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closePopup();
            }
          }}
        >
          <div className="bg-[var(--dash-surface)] border border-[var(--dash-border)] rounded-lg p-4 sm:p-6 md:p-8 w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl relative">
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 text-[var(--dash-text)] hover:text-[var(--dash-text-strong)] transition-colors p-1 cursor-pointer"
              aria-label="Close popup"
            >
              <i className="fas fa-times text-xl"></i>
            </button>

            <div className="flex items-center gap-3 mb-4 sm:mb-6 pr-8">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center shrink-0">
                <img src={toolImage} alt={toolName} width={35} />
              </div>
              <h2 className="text-[var(--dash-text-strong)] font-bold text-lg sm:text-xl">{toolName}</h2>
            </div>

            <p className="text-[var(--dash-text-strong)] text-xs sm:text-sm mb-4 sm:mb-6">
              Enter the payment email associated with your {toolName} subscription
            </p>

            {checkingStoredAuth ? (
              <div className="flex justify-center items-center py-8">
                <Loader />
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                <div>
                  <label className="block text-[var(--dash-text-strong)] font-bold text-xs sm:text-sm mb-2">
                    Payment Email
                  </label>
                  <Input
                    type="email"
                    value={paymentEmail}
                    onChange={(e) => setPaymentEmail(e.target.value)}
                    className="w-full bg-[var(--dash-input)] border-[var(--dash-border)] text-[var(--dash-text-strong)] rounded-md px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base focus:outline-none focus:border-[var(--theme-accent)]"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-[var(--dash-text-strong)] font-bold text-xs sm:text-sm mb-2">
                    OTP
                  </label>
                  <div className="flex flex-col sm:flex-row sm:items-stretch gap-2 sm:gap-3">
                    <Input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      placeholder="Enter OTP"
                      className="h-10 min-h-10 w-full flex-1 bg-[var(--dash-input)] border-[var(--dash-border)] text-[var(--dash-text-strong)] rounded-md px-3 sm:px-4 py-0 text-sm sm:text-base leading-normal focus:outline-none focus:border-[var(--theme-accent)] placeholder:text-[var(--dash-text-muted)]"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={handleSendOTP}
                      disabled={!paymentEmail || loading}
                      className="h-10 min-h-10 shrink-0 inline-flex items-center justify-center bg-[var(--dash-input)] border border-[var(--dash-border)] text-[var(--dash-text-strong)] rounded-md px-4 sm:px-6 text-sm sm:text-base font-semibold hover:bg-[var(--dash-surface-hover)] hover:border-[var(--theme-accent)] transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading && !otpSent ? "Sending..." : otpSent ? "Resend OTP" : "Send OTP"}
                    </button>
                  </div>
                  {otpSent && (
                    <p className="text-sm text-green-500 mt-2">OTP sent successfully! Check your email.</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={!paymentEmail || !otp || loading}
                  className="w-full bg-[var(--dash-input)] border border-[var(--dash-border)] text-[var(--dash-text-strong)] rounded-md py-2.5 sm:py-3 text-sm sm:text-base font-bold hover:bg-[var(--theme-accent)] hover:text-white transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Loading..." : "Submit"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default Popup;
